import axios from 'axios';
import { config } from '@/config';

/**
 * Crea un cliente axios para la API del dashboard de club.
 * Lee token y hash_teatro de localStorage en el momento de la llamada.
 *
 * @param {AbortSignal} [abortSignal] — señal opcional de AbortController
 * @returns {import('axios').AxiosInstance}
 */
export function createDashboardApiClient(abortSignal) {
    const token = localStorage.getItem('token') || '';
    const hashTeatro = localStorage.getItem('hash_teatro') || '';

    return axios.create({
        baseURL: config.API_TEATROS,
        headers: {
            Authorization: token,
            'Hash-Teatro': hashTeatro,
            Accept: 'application/json',
        },
        signal: abortSignal,
    });
}

/**
 * Obtiene la ocupación para una o varias temporadas.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getOccupancy(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/occupancy`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}

/**
 * Obtiene los datos de ventas para una o varias temporadas.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getSales(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/sales`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}

/**
 * Obtiene el perfil de afición para una o varias temporadas.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getFans(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/fans`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}

/**
 * Obtiene los partidos paginados y ordenables para una o varias temporadas.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {{ sort?: string, order?: string, perPage?: number, page?: number }} params
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getMatches(seasonIds, params, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/matches`, {
        params: {
            api_key: token,
            temporada_id: seasonIds,
            sort: params?.sort || 'revenue',
            order: params?.order || 'desc',
            per_page: params?.perPage || 15,
            page: params?.page || 1,
        },
    });
}

/**
 * Obtiene la clasificación de la división del club.
 * NOTA: NO usa temporada_id. Es división-only.
 *
 * @param {{ jornada?: number }} [params]
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: Array, meta: object }>}
 */
export async function getStandings(params, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    const queryParams = { api_key: token };
    if (params?.jornada) queryParams.jornada = params.jornada;

    return client.get(`/api/v1/teatro/dashboard/standings`, { params: queryParams });
}

/**
 * Obtiene los resultados de la división del club.
 * NOTA: NO usa temporada_id. Es división-only.
 *
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: Array, meta: object }>}
 */
export async function getResults(abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/results`, {
        params: { api_key: token },
    });
}

/**
 * Obtiene la actividad extra (eventos sin jornada) para una o varias temporadas.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getActivity(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/activity`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}

/**
 * Obtiene la lista de temporadas disponibles para el teatro.
 *
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: Array<{ nombre: string, activa: boolean }> }>}
 */
export async function getSeasons(abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/seasons`, {
        params: { api_key: token },
    });
}

/**
 * Obtiene el overview del dashboard para una o varias temporadas.
 * Axios serializa el array como `temporada_id[]=847&temporada_id[]=848`.
 *
 * @param {number[]} temporadaIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getOverview(temporadaIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/overview`, {
        params: { api_key: token, temporada_id: temporadaIds },
    });
}

/**
 * Obtiene los datos económicos del dashboard para una o varias temporadas.
 * Axios serializa el array como `season_id[]=847&season_id[]=848`.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getEconomic(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/economic`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}

/**
 * Obtiene los datos de evolución del dashboard para una o varias temporadas.
 * Muestra revenue, attendance, posición, eventos y racha por jornada.
 *
 * @param {number[]} seasonIds — array de IDs de temporada
 * @param {AbortSignal} [abortSignal]
 * @returns {Promise<{ data: { per_season: object, meta: object } }>}
 */
export async function getEvolution(seasonIds, abortSignal) {
    const token = localStorage.getItem('token') || '';
    const client = createDashboardApiClient(abortSignal);

    return client.get(`/api/v1/teatro/dashboard/evolution`, {
        params: { api_key: token, temporada_id: seasonIds },
    });
}
